import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import { formatCurrency, createSlug } from '../../utils/helpers';
import { prefetchStorefrontAvailabilityForToday } from '../storefront-booking/prefetchStorefrontAvailability';
import { warmStorefrontBusySlotsForToday } from '../storefront-booking/warmStorefrontBusySlots';
import { fetchStorefrontServices } from '../storefront-booking/storefrontServicesCache';
import { PageSkeleton } from '../../components/loading/skeletons';
import '../styles/LandingPage.css';
import '../styles/BookingFlow.css';
import '../styles/ServiceDetails.css';

const ServiceDetailsPage = ({ prefetchedService = null }) => {
  const { companyUrl, serviceSlug, storefrontSlug } = useParams();
  const resolvedSlug = serviceSlug || storefrontSlug;
  const navigate = useNavigate();
  const { companyData } = useOutletContext();
  const [service, setService] = useState(prefetchedService || null);
  const [loading, setLoading] = useState(!prefetchedService);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (prefetchedService) {
      setService(prefetchedService);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setService(null);

        const services = await fetchStorefrontServices(companyData.id);
        const serviceData = services.find((entry) => createSlug(entry.name) === resolvedSlug);

        if (!serviceData) {
          throw new Error('Service not found');
        }

        setService(serviceData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (companyData?.id && resolvedSlug) {
      fetchData();
    }
  }, [companyData?.id, resolvedSlug, prefetchedService]);

  useEffect(() => {
    if (companyData?.id) {
      void warmStorefrontBusySlotsForToday(companyData.id);
    }
  }, [companyData?.id]);

  useEffect(() => {
    if (!companyData?.id || !service?.id) {
      return;
    }

    void prefetchStorefrontAvailabilityForToday({
      companyData,
      service,
    });
  }, [companyData?.id, service?.id]);

  const handleBookNow = () => {
    if (companyData?.id && service?.id) {
      void prefetchStorefrontAvailabilityForToday({
        companyData,
        service,
      });
    }
    const serviceSlug = createSlug(service.name);
    navigate(`/book/${companyUrl}/${serviceSlug}?book=1`);
  };

  if (loading) {
    return (
      <div className="landing-page">
        <PageSkeleton padded variant="form" label="Loading service" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="landing-page">
        <div className="error-container">
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
          <Link to={`/book/${companyUrl}`} className="back-link">
            Return to Services
          </Link>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="landing-page">
        <div className="error-container">
          <h2>Service not found</h2>
          <p>The service you're looking for doesn't exist.</p>
          <Link to={`/book/${companyUrl}`} className="back-link">
            Return to Services
          </Link>
        </div>
      </div>
    );
  }

  const addons = service.addOns || [];
  const packages = service.packages || [];

  return (
    <div className="service-details-container">
      <div className="container">
        <header className="service-details-header">
          <div className="service-details-header-main">
            {service.imageUrl && (
              <img
                src={service.imageUrl}
                alt={service.name}
                className="service-details-image"
              />
            )}
            <div className="service-details-info">
              <h1>{service.name}</h1>
              {service.description && (
                <p className="service-description">{service.description}</p>
              )}
              <div className="service-details-meta">
                <div className="meta-item">
                  <span className="meta-icon">⏱️</span>
                  <span><strong>{service.duration || 60} minutes</strong></span>
                </div>
                <div className="meta-item">
                  <span className="meta-icon">💰</span>
                  <span><strong>{formatCurrency(service.price || 0)}</strong></span>
                </div>
              </div>
              <button type="button" className="book-now-button" onClick={handleBookNow}>
                Book Now
              </button>
            </div>
          </div>
        </header>

        {(addons.length > 0 || packages.length > 0) && (
          <div className="service-details-panel">
            {addons.length > 0 && (
              <section className="addons-section">
                <h2 className="section-title">Available Add-ons</h2>
                <div className="addons-grid">
                  {addons.map(addon => (
                    <div key={addon.id} className="addon-card">
                      {addon.imageUrl && (
                        <img
                          src={addon.imageUrl}
                          alt={addon.name}
                          className="addon-image"
                        />
                      )}
                      <div className="addon-content">
                        <h3>{addon.name}</h3>
                        {addon.description && (
                          <p>{addon.description}</p>
                        )}
                        <div className="addon-meta">
                          <span>{addon.duration || 0} min</span>
                          <span className="addon-price">{formatCurrency(addon.price || 0)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {packages.length > 0 && (
              <section className="packages-section">
                <h2 className="section-title">Available Packages</h2>
                <div className="packages-grid">
                  {packages.map(pkg => (
                    <div key={pkg.id} className="package-card">
                      {pkg.imageUrl && (
                        <img
                          src={pkg.imageUrl}
                          alt={pkg.name}
                          className="package-image"
                        />
                      )}
                      <div className="package-content">
                        <h3>{pkg.name}</h3>
                        {pkg.description && (
                          <p>{pkg.description}</p>
                        )}
                        <div className="package-details">
                          <div className="package-info">
                            <span>{pkg.sessions || 1} sessions</span>
                            <span>{pkg.duration || service.duration} min each</span>
                          </div>
                          {pkg.validity && (
                            <div className="package-validity">
                              Valid for {pkg.validity} days
                            </div>
                          )}
                          {pkg.discount > 0 && (
                            <div className="package-discount">
                              <span className="original-price">{formatCurrency(pkg.basePrice || pkg.price)}</span>
                              <span className="discount-badge">{pkg.discount}% OFF</span>
                            </div>
                          )}
                          <div className="package-price">
                            {formatCurrency(pkg.price || 0)}
                          </div>
                        </div>
                        {pkg.selectedAddons && pkg.selectedAddons.length > 0 && (
                          <div className="package-addons">
                            <strong>Includes:</strong>
                            <ul>
                              {pkg.selectedAddons.map(addon => (
                                <li key={addon.id}>{addon.name}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
      <div className="service-details-toolbar-outer">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate(`/book/${companyUrl}`)}
        >
          Back to Services
        </button>
      </div>
    </div>
  );
};

export default ServiceDetailsPage;

